import { useState } from "react";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiList,
  MiMenu,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { IDropTypeResponse } from "@miplatform/types";
import {
  mdiHexagonOutline,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { useDropTypes, usePermissions } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { SectionInfo } from "../../components/SectionInfo/SectionInfo";
import { useDropTypesViewModel } from "./useDropTypesViewModel";

export function DropTypes() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const { navigate } = useRouter();

  const {
    dropTypes,
    recentDropTypes,
    isLoading: isDropTypesLoading,
    getDropTypes,
    getRecentDropTypes,
  } = useDropTypes();

  const {
    selectedDropType,
    showDeleteModal,
    refresh,
    isLoading: isViewModelLoading,
    setSelectedDropType,
    setShowDeleteModal,
    handleDeleteDropType,
  } = useDropTypesViewModel();

  const isLoading = isDropTypesLoading || isViewModelLoading;

  const canCreateDropType = hasPermission(PERMISSIONS.DROP_TYPE.CREATE);
  const canUpdateDropType = hasPermission(PERMISSIONS.DROP_TYPE.UPDATE);
  const canDeleteDropType = hasPermission(PERMISSIONS.DROP_TYPE.DELETE);

  const sections = [
    {
      fieldset: [
        {
          renderValue: (data: IDropTypeResponse) => <SectionInfo data={data} />,
        },
      ],
    },
  ];

  const handleNavigate = (item: IDropTypeResponse) => {
    if (item.id) {
      navigate(`/estimating/pricing/drop-types/${item.id}`);
    }
  };

  const renderItemActions = (item: IDropTypeResponse) => {
    const actions = [];

    if (canUpdateDropType) {
      actions.push({
        label: BUTTON_LABELS.EDIT,
        icon: mdiPencilOutline,
        action: () => handleNavigate(item),
      });
    }

    if (canDeleteDropType) {
      actions.push({
        label: BUTTON_LABELS.DELETE,
        icon: mdiTrashCanOutline,
        action: () => handleOpenDeleteModal(item),
        color: theme.palette.red.main,
      });
    }

    return actions.length > 0 ? (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu data={actions} />
      </Box>
    ) : null;
  };

  const handleOpenDeleteModal = (item: IDropTypeResponse) => {
    setSelectedDropType(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedDropType) {
      handleDeleteDropType(selectedDropType);
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <MiBuildPageHeader
        title="Drop Types"
        loading={isLoading}
        titleIcon={
          <MiIcon path={mdiHexagonOutline} color={theme.palette.primary.main} />
        }
        actionComponent={
          canCreateDropType && (
            <MiButton
              title="Drop Type"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{ path: mdiPlus }}
              onClick={() => navigate("/estimating/pricing/add-drop-types")}
              data-testid="add-drop-type-btn"
            />
          )
        }
        search={search}
        onHandleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
        searchPlaceholder="Search"
        actionContainerStyle={{
          flexWrap: "nowrap",
        }}
      />
      <MiBox>
        <MiList
          viewType="card"
          cardProps={{
            sections,
            renderItemActions,
            onClick: handleNavigate,
            leftCardProps: { fieldset: [] },
          }}
          data={dropTypes}
          search={debouncedSearch}
          loading={isLoading}
          dataProvider={getDropTypes}
          recentData={recentDropTypes}
          recentDataProvider={getRecentDropTypes}
          paginationMode={PAGINATION.SERVER}
          refresh={refresh}
          emptyListProps={{
            text: "No Drop Types found",
          }}
          data-testid="drop-types-list"
        />
        <MiModalConfirm
          open={showDeleteModal}
          title={MODAL_TITLES.DELETE}
          textPositive={BUTTON_LABELS.DELETE}
          buttonPositiveColor={BUTTON_COLORS.RED}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.red.main,
          }}
          handleNegative={() => {
            setShowDeleteModal(false);
            setSelectedDropType(null);
          }}
          handlePositive={handleConfirmDeleteModal}
          description={`You are about to delete ${selectedDropType?.name}. Are you sure you would like to continue?`}
        />
      </MiBox>
    </>
  );
}
